import { useEffect, useRef } from 'react'
import { Platform } from 'react-native'
import { registerForPushNotifications, syncPushToken } from '../lib/notifications'

/**
 * Registers the device's Expo push token with the API once per session.
 *
 * Must only be mounted inside an authenticated layout (e.g. `(app)/_layout`).
 * Runs at most once per mount; a `useRef` flag prevents re-firing on
 * subsequent renders or auth-state flickers.
 *
 * - On web: no-op. Expo push is native-only.
 * - Permission denied: logs and skips silently. No second prompt this session.
 * - Network failure: logs and skips. Will retry on next app launch.
 */
export function useExpoPushSync(): void {
  const hasSynced = useRef(false)

  useEffect(() => {
    if (Platform.OS === 'web') return
    if (hasSynced.current) return

    hasSynced.current = true

    registerForPushNotifications()
      .then((token) => {
        if (!token) {
          // Permission denied or no EAS project ID — skip silently.
          console.log('[push] token not available, skipping sync')
          return
        }
        return syncPushToken(token).catch((err: unknown) => {
          console.warn('[push] failed to sync token, will retry on next launch', err)
        })
      })
      .catch((err: unknown) => {
        console.warn('[push] registerForPushNotifications threw unexpectedly', err)
      })
  }, [])
}
